import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import firebaseAnalyticsService from '../services/firebaseAnalyticsService';
import metaPixelService from '../services/metaPixelService';
import { META_PIXEL_CONFIG, getEventConfig } from '../config/metaPixelConfig';

// En la Mac puede apuntar al emulador (REACT_APP_FUNCTIONS_URL, ver README "Probar en local").
const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-transferisok.cloudfunctions.net';

/**
 * Dice si el state que volvió de Mercado Pago es de un alta o de una reautorización.
 * Solo elige a qué endpoint mandarlo: la firma la verifica el backend.
 */
function esStateDeAlta(state) {
    try {
        const cuerpo = state.split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(cuerpo + '='.repeat((4 - (cuerpo.length % 4)) % 4)));
        return Boolean(payload.alta);
    } catch (e) {
        return false;
    }
}

function AuthCallback() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [retryLink, setRetryLink] = useState(null);
    // El code de Mercado Pago es de un solo uso: que el efecto doble de React en
    // desarrollo (StrictMode) no lo mande dos veces.
    const procesado = useRef(false);

    useEffect(() => {
        if (procesado.current) return;
        procesado.current = true;

        /**
         * Completa la reautorización de una cuenta que ya existe.
         * No crea usuarios ni toca a los operadores: el backend actualiza el token en
         * un solo lugar y todos los que comparten la cuenta lo toman de ahí.
         */
        const handleReauth = async (code, state) => {
            try {
                if (!code) {
                    setError('No se recibió el código de autorización de Mercado Pago.');
                    setLoading(false);
                    return;
                }

                const { data } = await axios.post(`${FUNCTIONS_URL}/completeReauth`, { code, state });

                if (!data.success) {
                    throw new Error(data.error || 'Error en la reautorización');
                }

                setSuccess(true);
                setLoading(false);
            } catch (err) {
                console.error('Error en la reautorización:', err.response?.data?.error || err.message);
                setLoading(false);

                // El backend rechaza si se autorizó con una cuenta de MP distinta a la
                // registrada, para no mezclar los datos de dos comercios.
                if (err.response?.status === 409) {
                    setError(err.response.data.message);
                } else if (err.response?.data?.error === 'code_vencido') {
                    // Caso frecuente: recargar el callback o volver atrás reusa un code
                    // que Mercado Pago ya consumió. Se ofrece reintentar desde cero.
                    setError(err.response.data.message);
                    const state = new URLSearchParams(location.search).get('state');
                    if (state) {
                        setRetryLink({ href: `/reauth?t=${encodeURIComponent(state)}`, texto: 'Volver a intentar' });
                    }
                } else {
                    setError(
                        err.response?.data?.message ||
                        err.response?.data?.error ||
                        'No pudimos completar la reconexión. Escribinos y lo resolvemos.'
                    );
                }
            }
        };

        /**
         * Completa el alta. Los datos del registro ya están en el backend
         * (iniciarAlta, desde Negocio.js): acá solo viajan el code y el state.
         */
        const handleAlta = async (code, state) => {
            try {
                if (!code) {
                    firebaseAnalyticsService.trackRegistro('registro_error', { paso: 'mercado_pago', codigo: 'sin_autorizacion' });
                    setError('No se completó la autorización en Mercado Pago. Volvé a intentar el registro.');
                    setRetryLink({ href: '/registro/negocio', texto: 'Volver a intentar' });
                    setLoading(false);
                    return;
                }

                const { data } = await axios.post(`${FUNCTIONS_URL}/completarAlta`, { code, state });

                if (!data.success) {
                    throw new Error(data.message || 'Error en el registro');
                }

                localStorage.removeItem('pendingUserData');
                // De la versión anterior del registro, que guardaba todo en el navegador.
                localStorage.removeItem('pendingBusinessData');
                localStorage.removeItem('userEmail');

                // Recién acá el registro está completo: la cuenta quedó creada. A GA4 el
                // sign_up lo manda completarAlta, desde el servidor.
                metaPixelService.initializePixel(META_PIXEL_CONFIG.pixelId);
                metaPixelService.trackCompleteRegistration(getEventConfig('completeRegistration', 'finished'));

                setSuccess(true);
                setLoading(false);

                // Esperar 3 segundos antes de redirigir a la página de bienvenida
                setTimeout(() => {
                    navigate('/bienvenida');
                }, 3000);
            } catch (err) {
                const codigo = err.response?.data?.error;
                console.error('Error en el alta:', codigo || err.message);
                firebaseAnalyticsService.trackRegistro('registro_error', { paso: 'mercado_pago', codigo: codigo || 'error_inesperado' });
                setLoading(false);
                setError(
                    err.response?.data?.message ||
                    'No pudimos completar el registro. Te vamos a contactar para terminarlo.'
                );

                if (codigo === 'ya_registrado' || codigo === 'registro_usado') {
                    setRetryLink({ href: '/login', texto: 'Iniciar sesión' });
                } else if (codigo === 'code_vencido' || codigo === 'state_invalido') {
                    setRetryLink({ href: '/registro/negocio', texto: 'Volver a intentar' });
                } else {
                    // Error del servidor o de Mercado Pago: ya se avisó por Telegram.
                    setTimeout(() => {
                        navigate('/registro-error');
                    }, 5000);
                }
            }
        };

        const handleCallback = async () => {
            const params = new URLSearchParams(location.search);
            const code = params.get('code');
            const state = params.get('state');

            // Sin state no se sabe de qué registro es: el alta ya no toma los datos
            // del navegador.
            if (!state) {
                setError('El link de Mercado Pago no es válido. Volvé a completar los datos del negocio.');
                setRetryLink({ href: '/registro/negocio', texto: 'Volver a intentar' });
                setLoading(false);
                return;
            }

            // Alta o reautorización de una cuenta existente: el contexto viaja firmado en
            // el state, así que no depende del localStorage ni de que haya sesión.
            if (esStateDeAlta(state)) {
                await handleAlta(code, state);
            } else {
                await handleReauth(code, state);
            }
        };

        handleCallback();
    }, [location, navigate]);

    return (
        <div className="login-container">
            <div className="login-card">
                {loading ? (
                    <div className="auth-loading-container">
                        <div className="auth-loader"></div>
                        <h2>Conectando con Mercado Pago</h2>
                        <p>Estamos verificando tu cuenta. Por favor espera un momento...</p>
                    </div>
                ) : (
                    <>
                        <h2>{success ? "Conexión con Mercado Pago" : "Estado de la conexión"}</h2>
                        {error && (
                            <div className={success ? "success-message" : "error-message"}>
                                <p>{error}</p>
                                {retryLink && (
                                    <p>
                                        <a href={retryLink.href} className="reauth-retry">
                                            {retryLink.texto}
                                        </a>
                                    </p>
                                )}
                                {success ? (
                                    <p>Redirigiendo a la página de bienvenida en 3 segundos...</p>
                                ) : !retryLink && (
                                    <p>Un momento por favor...</p>
                                )}
                            </div>
                        )}
                        {success && !error && (
                            <div className="success-message">
                                <p>¡Autenticación exitosa!</p>
                                <p>Redirigiendo a la página de bienvenida en 3 segundos...</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            <style jsx>{`
                .auth-loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 30px;
                    text-align: center;
                }
                
                .auth-loader {
                    border: 5px solid #f3f3f3;
                    border-top: 5px solid #001c4d;
                    border-radius: 50%;
                    width: 50px;
                    height: 50px;
                    animation: spin 1.5s linear infinite;
                    margin-bottom: 20px;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .auth-loading-container h2 {
                    color: #001c4d;
                    margin-bottom: 15px;
                }
                
                .reauth-retry {
                    display: inline-block;
                    background: #009ee3;
                    color: #fff;
                    text-decoration: none;
                    padding: 12px 28px;
                    border-radius: 8px;
                    font-weight: 600;
                    margin-top: 8px;
                }

                .auth-loading-container p {
                    color: #64748b;
                    font-size: 16px;
                    max-width: 400px;
                }
                
                .success-message, .error-message {
                    padding: 15px;
                    border-radius: 8px;
                    margin: 15px 0;
                    text-align: center;
                }
                
                .success-message {
                    background-color: #e6f7ee;
                    color: #0d6832;
                }
                
                .error-message {
                    background-color: #fdecea;
                    color: #e74c3c;
                }
            `}</style>
        </div>
    );
}

export default AuthCallback; 